/**
 * GameLogFilterPanel — the game log's filters, always fully expanded: player
 * chips, event-kind chips and the sort. Opened from the log controls row
 * (GameLogControls) beside the chat input; the filters themselves live on the
 * game screen so the in-place log and its grown sheet share them.
 *
 * The row's ALL | CHAT toggle owns the view. CHAT supplies only message
 * authors and no event kinds; ALL supplies the roster and present log kinds.
 */
import React from 'react'
import { ScrollView, Text, TouchableOpacity, View } from 'react-native'
import type { GamePlayerResponse } from '@warcarp/api-types'
import { PLAYER_COLORS } from '@warcarp/ui'
import {
  DEFAULT_LOG_FILTERS,
  describeActiveFilters,
  hasActiveFilters,
  LOG_FILTER_KIND_LABEL,
  type ActivityFilterState,
  type LogFilterKind,
} from '../../lib/activityFilters'
import { playerColorSlot } from '../../lib/playerColor'
import { FilterChip } from './FilterChip'

function ChipRow({ children }: { children: React.ReactNode }) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={{ flexDirection: 'row', alignItems: 'center' }}
    >
      {children}
    </ScrollView>
  )
}

export function GameLogFilterPanel({
  players,
  filters,
  onChange,
  kinds,
}: {
  players: readonly GamePlayerResponse[]
  filters: ActivityFilterState
  onChange: (next: ActivityFilterState) => void
  /** The kinds the log has, in chip order (lib/activityFilters presentLogKinds). */
  kinds: readonly LogFilterKind[]
}) {
  const set = (patch: Partial<ActivityFilterState>) => onChange({ ...filters, ...patch })
  const sortedPlayers = [...players].sort((a, b) => a.slot - b.slot)
  return (
    <View testID="game-log-filters" className="pb-1 gap-0.5">
      <ChipRow>
        <FilterChip label="All players" active={filters.playerFilter === null} onPress={() => set({ playerFilter: null })} />
        {sortedPlayers.map((player) => {
          const slot = String(player.slot)
          return (
            <FilterChip
              key={slot}
              label={player.displayName}
              active={filters.playerFilter === slot}
              onPress={() => set({ playerFilter: filters.playerFilter === slot ? null : slot })}
              dotColor={PLAYER_COLORS[playerColorSlot(player)]?.hex}
            />
          )
        })}
      </ChipRow>
      {kinds.length > 0 ? (
        <ChipRow>
          <FilterChip label="All events" active={filters.kindFilter === null} onPress={() => set({ kindFilter: null })} />
          {kinds.map((kind) => (
            <FilterChip
              key={kind}
              label={LOG_FILTER_KIND_LABEL[kind]}
              active={filters.kindFilter === kind}
              onPress={() => set({ kindFilter: filters.kindFilter === kind ? null : kind })}
            />
          ))}
        </ChipRow>
      ) : null}
      <ChipRow>
        <FilterChip
          label={filters.newestFirst ? 'Newest first' : 'Oldest first'}
          active
          onPress={() => set({ newestFirst: !filters.newestFirst })}
        />
        {hasActiveFilters(filters) ? (
          <FilterChip testID="game-log-clear-filters" label="Clear ✕" active={false} onPress={() => onChange(DEFAULT_LOG_FILTERS)} />
        ) : null}
      </ChipRow>
    </View>
  )
}

/**
 * The active filters in short form, on a thin tinted line, while the chips are
 * closed: "Filtered: yakuza · Attack · Oldest first". A tap opens the chips;
 * ✕ clears them. Renders nothing when no filter is active.
 */
export function GameLogFilterSummary({
  players,
  filters,
  chatOnly,
  onOpen,
  onClear,
}: {
  players: readonly GamePlayerResponse[]
  filters: ActivityFilterState
  /** CHAT: kinds don't apply to chat lines, so they aren't listed. */
  chatOnly: boolean
  onOpen: () => void
  onClear: () => void
}) {
  const applied = chatOnly ? { ...filters, kindFilter: null } : filters
  if (!hasActiveFilters(applied)) return null
  const labels = describeActiveFilters({
    ...applied,
    playerLabel: (slot) => players.find((player) => String(player.slot) === slot)?.displayName ?? `Player ${slot}`,
    kindLabel: (kind) => LOG_FILTER_KIND_LABEL[kind as LogFilterKind] ?? kind,
  })
  return (
    <View
      testID="game-log-filter-summary"
      className="flex-row items-center bg-blood/10 border border-blood"
      style={{ minHeight: 24, paddingLeft: 8, width: '100%' }}
    >
      <TouchableOpacity
        onPress={onOpen}
        accessibilityRole="button"
        accessibilityLabel={`Filtered: ${labels.join(', ')}. Show log filters`}
        style={{ flex: 1, paddingVertical: 3 }}
      >
        <Text className="text-blood font-typed text-[11px]" numberOfLines={1}>
          Filtered: {labels.join(' · ')}
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        onPress={onClear}
        accessibilityRole="button"
        accessibilityLabel="Clear log filters"
        testID="game-log-filter-summary-clear"
        style={{ paddingHorizontal: 8, paddingVertical: 3 }}
      >
        <Text className="text-blood font-typed text-[11px]">✕</Text>
      </TouchableOpacity>
    </View>
  )
}
